import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Info, RotateCcw, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { StatePill } from "@/monitoring/components/primitives";
import { percentDigits, sameValue, snapValue } from "../model";
import type { EvaluationSetting } from "../types";
import type { SettingText } from "../use-setting-text";
import { DistributionPanel, type DistributionState } from "./distribution-panel";

/** small marks next to a setting name: saved override, unsaved edit, shadow mode */
function SettingBadges({
  overridden,
  dirty,
  shadow,
}: {
  overridden: boolean;
  dirty: boolean;
  shadow?: boolean;
}) {
  const { t } = useLocalization();
  if (!overridden && !dirty && !shadow) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {dirty && (
        <span className="inline-flex h-5 items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
          {t("evalSettings.card.unsaved")}
        </span>
      )}
      {shadow && (
        <StatePill tone="warn" className="h-5 px-1.5 text-[10px] font-semibold uppercase tracking-wide">
          {t("evalSettings.enforce.off")}
        </StatePill>
      )}
      {overridden && (
        <span className="inline-flex h-5 items-center rounded-md border px-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {t("evalSettings.card.overridden")}
        </span>
      )}
    </div>
  );
}

function SettingTitle({ text, setting, children }: { text: SettingText; setting: EvaluationSetting; children?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <h3 className="text-sm font-semibold leading-tight">{text.label(setting.key)}</h3>
      <code className="break-all font-mono text-[11px] text-muted-foreground">{setting.key}</code>
      {children}
    </div>
  );
}

/** the threshold as a number in % (typed freely, snapped to the step on blur) */
function PercentInput({
  setting,
  value,
  onChange,
  label,
}: {
  setting: EvaluationSetting;
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  const digits = percentDigits(setting.step);
  const display = (v: number) => String(Number((v * 100).toFixed(digits)));
  const [text, setText] = useState(() => display(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(String(Number((value * 100).toFixed(digits))));
  }, [value, digits]);

  return (
    <div className="relative w-[5.75rem] shrink-0">
      <Input
        type="number"
        inputMode="decimal"
        aria-label={label}
        value={text}
        min={(setting.min ?? 0) * 100}
        max={(setting.max ?? 1) * 100}
        step={(setting.step ?? 0.01) * 100}
        onFocus={() => {
          focused.current = true;
        }}
        onChange={(event) => {
          setText(event.target.value);
          const parsed = Number(event.target.value.replace(",", "."));
          if (event.target.value.trim() !== "" && Number.isFinite(parsed)) onChange(snapValue(parsed / 100, setting));
        }}
        onBlur={() => {
          focused.current = false;
          setText(display(value));
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") (event.target as HTMLInputElement).blur();
        }}
        className="h-10 pr-7 text-right font-mono text-base tabular-nums [appearance:textfield] md:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
    </div>
  );
}

/** a count (pieces) as a whole number, typed freely and snapped to the step */
function CountInput({
  setting,
  value,
  onChange,
  label,
}: {
  setting: EvaluationSetting;
  value: number;
  onChange: (value: number) => void;
  label: string;
}) {
  const { t } = useLocalization();
  const [text, setText] = useState(() => String(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(String(value));
  }, [value]);

  return (
    <div className="relative w-[5.75rem] shrink-0">
      <Input
        type="number"
        inputMode="numeric"
        aria-label={label}
        value={text}
        min={setting.min ?? 1}
        max={setting.max ?? 20}
        step={setting.step ?? 1}
        onFocus={() => {
          focused.current = true;
        }}
        onChange={(event) => {
          setText(event.target.value);
          const parsed = Number(event.target.value);
          if (event.target.value.trim() !== "" && Number.isFinite(parsed)) onChange(snapValue(parsed, setting));
        }}
        onBlur={() => {
          focused.current = false;
          setText(String(value));
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") (event.target as HTMLInputElement).blur();
        }}
        className="h-10 pr-8 text-right font-mono text-base tabular-nums [appearance:textfield] md:text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        {t("evalSettings.card.pieces")}
      </span>
    </div>
  );
}

/** default value and the buttons back to it / back to the saved value */
function DefaultLine({
  text,
  setting,
  value,
  dirty,
  onReset,
  onUndo,
}: {
  text: SettingText;
  setting: EvaluationSetting;
  value: number | boolean;
  dirty: boolean;
  onReset: () => void;
  onUndo: () => void;
}) {
  const { t } = useLocalization();
  const isDefault = sameValue(value, setting.default);
  // when the saved value is the default, "reset" already undoes the edit
  const showUndo = dirty && (isDefault || !sameValue(setting.value, setting.default));
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span className="tabular-nums">
        {isDefault ? t("evalSettings.card.isDefault") : t("evalSettings.card.default", { value: text.value(setting.key, setting.default) })}
      </span>
      {!isDefault && (
        <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 text-xs" onClick={onReset}>
          <RotateCcw className="h-3.5 w-3.5" />
          {t("evalSettings.card.reset")}
        </Button>
      )}
      {showUndo && (
        <Button variant="ghost" size="sm" className="h-8 gap-1 px-2 text-xs" onClick={onUndo}>
          <Undo2 className="h-3.5 w-3.5" />
          {t("evalSettings.card.undo")}
          <span className="tabular-nums text-muted-foreground">({text.value(setting.key, setting.value)})</span>
        </Button>
      )}
    </div>
  );
}

export interface FlagControl {
  setting: EvaluationSetting;
  /** edited value */
  value: boolean;
  dirty: boolean;
  onChange: (value: boolean) => void;
}

/** "Enforced" vs "Shadow mode" switch of a threshold */
function EnforceSwitch({ control, text }: { control: FlagControl; text: SettingText }) {
  const { t } = useLocalization();
  const id = useId();
  const enforced = control.value;
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 transition-colors",
        enforced ? "border-border/70 hover:bg-muted/30" : "border-amber-500/35 bg-amber-500/[0.06]",
      )}
    >
      <Switch
        id={id}
        checked={enforced}
        onCheckedChange={(checked) => control.onChange(checked)}
        aria-label={text.label(control.setting.key)}
        className="mt-0.5"
      />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={cn("text-sm font-medium", !enforced && "text-amber-300")}>
            {enforced ? t("evalSettings.enforce.on") : t("evalSettings.enforce.off")}
          </span>
          <code className="font-mono text-[11px] text-muted-foreground">{control.setting.key}</code>
          {control.dirty && <span className="h-1.5 w-1.5 rounded-full bg-primary" title={t("evalSettings.card.unsaved")} />}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {enforced ? t("evalSettings.enforce.onHint") : t("evalSettings.enforce.offHint")}
        </span>
        {!sameValue(control.value, control.setting.default) && (
          <span className="mt-0.5 block text-[11px] text-muted-foreground">
            {t("evalSettings.card.default", { value: text.value(control.setting.key, control.setting.default) })}
          </span>
        )}
      </span>
    </label>
  );
}

/**
 * One threshold: name and meaning, the value in % or pieces (input + slider), its default, the paired
 * enforce switch, and the histogram of the metric it cuts (``distribution`` null: a threshold without a
 * stored metric, no histogram). A threshold whose flag is off is dimmed.
 */
export function ThresholdCard({
  setting,
  value,
  dirty,
  onChange,
  enforce,
  requires,
  distribution,
  failColor,
  outcome,
  text,
}: {
  setting: EvaluationSetting;
  /** edited value */
  value: number;
  dirty: boolean;
  onChange: (value: number) => void;
  enforce: FlagControl | null;
  /** the flag this threshold only matters with, with its edited value */
  requires: { setting: EvaluationSetting; value: boolean } | null;
  distribution: DistributionState | null;
  failColor: string;
  outcome: string;
  text: SettingText;
}) {
  const { t } = useLocalization();
  const description = text.description(setting.key);
  const inactive = !!requires && !requires.value;
  const shadow = !!enforce && !enforce.value;
  const saved = typeof setting.value === "number" ? setting.value : value;
  const min = setting.min ?? 0;
  const max = setting.max ?? 1;
  const step = setting.step ?? 0.01;

  return (
    <Card id={`setting-${setting.key}`} className="min-w-0 scroll-mt-28 overflow-hidden">
      {inactive && (
        <p className="flex items-start gap-1.5 border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {t("evalSettings.requires.off", { flag: text.label(requires!.setting.key) })}
        </p>
      )}
      <div className={cn("grid", distribution && "lg:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]")}>
        <div className={cn("flex min-w-0 flex-col gap-3 p-4", inactive && "opacity-60")}>
          <div className="flex flex-col gap-1.5">
            <SettingTitle text={text} setting={setting} />
            <SettingBadges overridden={setting.overridden} dirty={dirty} shadow={shadow} />
            {description && <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>}
          </div>

          <div className={cn("flex items-center gap-3", !distribution && "max-w-xl")}>
            {setting.unit === "count" ? (
              <CountInput
                setting={setting}
                value={value}
                onChange={onChange}
                label={t("evalSettings.card.input", { label: text.label(setting.key) })}
              />
            ) : (
              <PercentInput
                setting={setting}
                value={value}
                onChange={onChange}
                label={t("evalSettings.card.input", { label: text.label(setting.key) })}
              />
            )}
            <Slider
              min={min}
              max={max}
              step={step}
              value={[value]}
              onValueChange={([next]) => onChange(snapValue(next, setting))}
              aria-label={text.label(setting.key)}
              className="flex-1 py-3 [&_[role=slider]]:h-5 [&_[role=slider]]:w-5"
            />
          </div>
          <div className="-mt-1 flex flex-col gap-1">
            <div className="text-xs">
              <span className="font-mono tabular-nums text-foreground/90">{text.rule(setting, value)}</span>
              <span className="text-muted-foreground"> → {outcome}</span>
            </div>
            <DefaultLine
              text={text}
              setting={setting}
              value={value}
              dirty={dirty}
              onReset={() => onChange(setting.default as number)}
              onUndo={() => onChange(saved)}
            />
          </div>

          {enforce && <EnforceSwitch control={enforce} text={text} />}
        </div>

        {distribution && (
          <div className="min-w-0 border-t p-4 lg:border-l lg:border-t-0">
            <DistributionPanel
              setting={setting}
              value={value}
              saved={saved}
              failColor={failColor}
              outcome={outcome}
              shadow={shadow}
              state={distribution}
              text={text}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

/** A switch of its own (not the enforce flag of a threshold), e.g. "pizza on a plate needs no saver". */
export function FlagCard({ control, text }: { control: FlagControl; text: SettingText }) {
  const id = useId();
  const { setting, value } = control;
  const description = text.description(setting.key);
  return (
    <Card id={`setting-${setting.key}`} className="min-w-0 scroll-mt-28 overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <Switch
          id={id}
          checked={value}
          onCheckedChange={(checked) => control.onChange(checked)}
          className="mt-0.5"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <label htmlFor={id} className="cursor-pointer">
            <SettingTitle text={text} setting={setting}>
              <span className={cn("text-xs font-medium", value ? "text-foreground" : "text-muted-foreground")}>
                {text.value(setting.key, value)}
              </span>
            </SettingTitle>
          </label>
          <SettingBadges overridden={setting.overridden} dirty={control.dirty} />
          {description && <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>}
          <DefaultLine
            text={text}
            setting={setting}
            value={value}
            dirty={control.dirty}
            onReset={() => control.onChange(setting.default as boolean)}
            onUndo={() => control.onChange(setting.value as boolean)}
          />
        </div>
      </div>
    </Card>
  );
}
